import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { FIREBASE_ENV } from '@/config/firebaseConfig';

/**
 * Crash reporting. Gated on DSN presence rather than environment, so it can be
 * switched on deliberately for a staging or device build without a code change
 * — but see the environment tag below, which keeps those events separable.
 *
 * The DSN has to be present at BUILD time (it is inlined via vite.config.ts's
 * define block), not at runtime. For the Android app that means SENTRY_DSN must
 * be in the environment for the `npm run build` that precedes `cap sync`, or the
 * bundle inside the APK ships with no crash reporting at all.
 */
const DSN = process.env.SENTRY_DSN || '';

export function initSentry() {
  if (!DSN) return;
  Sentry.init({
    dsn: DSN,
    // Tagged off the app's own environment rather than Vite's mode, which
    // reports "production" for any non-dev build — including previews and
    // dev-project device builds, whose crashes would otherwise be
    // indistinguishable from real users'.
    environment: import.meta.env.DEV ? 'development' : FIREBASE_ENV,
    // Separates "crashed in the Android WebView" from "crashed in a browser"
    // without having to infer it from the user agent.
    initialScope: {
      tags: { platform: Capacitor.getPlatform() },
    },
    // No tracesSampleRate / replays*SampleRate here on purpose. Those were
    // set previously and did nothing: @sentry/react only sends traces with
    // browserTracingIntegration() and replays with replayIntegration(), and
    // neither was ever added — so the settings read as "tracing and replay
    // are on" while nothing was collected.
    //
    // Adding replay in particular is a decision rather than a config tweak:
    // it records what the user sees, which here means their whole cellar,
    // and it becomes a Play data-safety declaration. Email was deliberately
    // removed from these events for the same reason.
  });
}

/**
 * Identifies the crashing user by uid only.
 *
 * Email was previously attached to every event. The uid is enough to find the
 * account, and not sending email keeps personal data out of a third party —
 * which also keeps it off the Play data-safety declaration.
 */
export function setSentryUser(uid: string | null) {
  if (!DSN) return;
  Sentry.setUser(uid ? { id: uid } : null);
}

export function setSentryContext(key: string, data: Record<string, unknown>) {
  if (!DSN) return;
  Sentry.setContext(key, data);
}
